/**
 * JoinsStep - Step 2: Configure table joins using relationships
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  IconButton,
  Select,
  Option,
  Sheet,
  Chip,
  Alert,
  Tooltip,
  CircularProgress,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemDecorator,
  ListItemContent,
  Radio,
  RadioGroup,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { useViewBuilderContext } from "../ViewBuilderContext";
import {
  useRelationships,
  getOtherTableId,
} from "../../../hooks/useRelationships";
import { useSchemas } from "../../../hooks/useSchemas";
import type {
  SchemaRelationshipResponse,
  ViewBuilderJoin,
  JoinType,
} from "../../../types";

interface JoinsStepProps {
  workspaceId: string;
}

// State for the nested join picker modal
interface NestedJoinPickerState {
  isOpen: boolean;
  parentJoinId: string | null;
  sourceTableId: string;
  sourceTableName: string;
  availableRelationships: SchemaRelationshipResponse[];
  selectedRelationshipId: string | null;
}

export default function JoinsStep({ workspaceId }: JoinsStepProps) {
  const {
    state,
    loadRelationships,
    addJoin,
    updateJoin,
    removeJoin,
    getAvailableRelationships,
  } = useViewBuilderContext();

  const {
    relationships,
    loading: relationshipsLoading,
    error: relationshipsError,
  } = useRelationships(workspaceId);
  const { schemas, loading: schemasLoading } = useSchemas(workspaceId);

  // State for nested join picker modal
  const [nestedPicker, setNestedPicker] = useState<NestedJoinPickerState>({
    isOpen: false,
    parentJoinId: null,
    sourceTableId: "",
    sourceTableName: "",
    availableRelationships: [],
    selectedRelationshipId: null,
  });

  // Load relationships into context when they're fetched
  useEffect(() => {
    if (relationships.length > 0) {
      loadRelationships(relationships);
    }
  }, [relationships, loadRelationships]);

  // Get available relationships for the focus table
  const availableForFocusTable = useMemo(() => {
    if (!state.focusTableId) return [];
    return getAvailableRelationships(state.focusTableId);
  }, [state.focusTableId, getAvailableRelationships]);

  // Get available relationships for each joined table (for nested joins)
  const getAvailableForJoinedTable = (
    joinId: string,
  ): SchemaRelationshipResponse[] => {
    const join = state.selectedJoins.find((j) => j.id === joinId);
    if (!join) return [];
    return getAvailableRelationships(join.targetTableId);
  };

  const handleAddJoin = (
    relationship: SchemaRelationshipResponse,
    parentJoinId: string | null,
  ) => {
    addJoin(relationship, parentJoinId, "LEFT", schemas);
  };

  const handleJoinTypeChange = (joinId: string, joinType: JoinType) => {
    updateJoin(joinId, { joinType });
  };

  // Open the nested join picker modal
  const openNestedJoinPicker = (joinId: string) => {
    const join = state.selectedJoins.find((j) => j.id === joinId);
    if (!join) return;

    const available = getAvailableRelationships(join.targetTableId);
    setNestedPicker({
      isOpen: true,
      parentJoinId: joinId,
      sourceTableId: join.targetTableId,
      sourceTableName: join.targetTableName,
      availableRelationships: available,
      selectedRelationshipId: null,
    });
  };

  // Handle selection in the nested join picker
  const handleNestedJoinSelect = () => {
    if (!nestedPicker.selectedRelationshipId) return;

    const relationship = nestedPicker.availableRelationships.find(
      (r) => r.id === nestedPicker.selectedRelationshipId,
    );
    if (!relationship) return;

    handleAddJoin(relationship, nestedPicker.parentJoinId);
    setNestedPicker({
      isOpen: false,
      parentJoinId: null,
      sourceTableId: "",
      sourceTableName: "",
      availableRelationships: [],
      selectedRelationshipId: null,
    });
  };

  // Close the nested join picker
  const closeNestedJoinPicker = () => {
    setNestedPicker({
      isOpen: false,
      parentJoinId: null,
      sourceTableId: "",
      sourceTableName: "",
      availableRelationships: [],
      selectedRelationshipId: null,
    });
  };

  const getTableName = (tableId: string): string => {
    const table = schemas.find((s) => s.id === tableId);
    return table?.display_name || table?.table_name || "Unknown";
  };

  const getColumnName = (tableId: string, columnId: string): string => {
    const table = schemas.find((s) => s.id === tableId);
    const column = table?.columns?.find((c) => c.id === columnId);
    return column?.display_name || column?.column_name || "?";
  };

  // Build display info for a relationship
  const getRelationshipInfo = (
    rel: SchemaRelationshipResponse,
    sourceTableId: string,
  ) => {
    const targetTableId = getOtherTableId(rel, sourceTableId);
    const sourceColumn =
      rel.from_table_id === sourceTableId
        ? rel.from_column_id
        : rel.to_column_id;
    const targetColumn =
      rel.from_table_id === sourceTableId
        ? rel.to_column_id
        : rel.from_column_id;

    return {
      targetTableId,
      targetTableName: getTableName(targetTableId),
      sourceColumnName: getColumnName(sourceTableId, sourceColumn),
      targetColumnName: getColumnName(targetTableId, targetColumn),
    };
  };

  // Group joins by parent for hierarchical display
  const rootJoins = state.selectedJoins.filter((j) => j.parentJoinId === null);
  const getChildJoins = (parentId: string) =>
    state.selectedJoins.filter((j) => j.parentJoinId === parentId);

  const loading = relationshipsLoading || schemasLoading;

  if (!state.focusTableId) {
    return (
      <Alert color="warning">
        Please select a focus table in Step 1 before configuring joins.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {relationshipsError && <Alert color="danger">{relationshipsError}</Alert>}

      {/* Info Card */}
      <Card variant="soft" color="primary">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center">
            <AccountTreeIcon />
            <Box>
              <Typography level="title-sm">Join Related Tables</Typography>
              <Typography level="body-xs">
                Add joins to include data from related tables. Joins are
                optional - you can skip this step if you only need data from the
                focus table.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Current Joins */}
      {state.selectedJoins.length > 0 && (
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>
              Configured Joins ({state.selectedJoins.length})
            </Typography>

            <Stack spacing={2}>
              {rootJoins.map((join) => (
                <JoinNode
                  key={join.id}
                  join={join}
                  childJoins={getChildJoins(join.id)}
                  availableRelationships={getAvailableForJoinedTable(join.id)}
                  onAddNestedJoin={() => openNestedJoinPicker(join.id)}
                  onRemove={() => removeJoin(join.id)}
                  onTypeChange={(type) => handleJoinTypeChange(join.id, type)}
                  getTableName={getTableName}
                  getRelationshipInfo={getRelationshipInfo}
                  getChildJoins={getChildJoins}
                  getAvailableForJoinedTable={getAvailableForJoinedTable}
                  openNestedJoinPicker={openNestedJoinPicker}
                  removeJoin={removeJoin}
                  handleJoinTypeChange={handleJoinTypeChange}
                  depth={0}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Available Relationships */}
      <Card variant="outlined">
        <CardContent>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Available Relationships from {getTableName(state.focusTableId)}
          </Typography>

          {availableForFocusTable.length === 0 ? (
            <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
              {relationships.length === 0
                ? "No relationships defined in the database schema."
                : "All available relationships have been added as joins."}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {availableForFocusTable.map((rel) => {
                const info = getRelationshipInfo(rel, state.focusTableId!);
                return (
                  <Sheet
                    key={rel.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: "md",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <LinkIcon sx={{ color: "neutral.500" }} />
                    <Box sx={{ flex: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography level="body-sm" fontWeight="md">
                          {getTableName(state.focusTableId!)}
                        </Typography>
                        <Chip size="sm" variant="soft" color="neutral">
                          {info.sourceColumnName}
                        </Chip>
                        <ArrowRightAltIcon sx={{ color: "neutral.400" }} />
                        <Typography level="body-sm" fontWeight="md">
                          {info.targetTableName}
                        </Typography>
                        <Chip size="sm" variant="soft" color="neutral">
                          {info.targetColumnName}
                        </Chip>
                      </Stack>
                      {rel.relationship_name && (
                        <Typography
                          level="body-xs"
                          sx={{ color: "text.tertiary", mt: 0.5 }}
                        >
                          {rel.relationship_name}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      size="sm"
                      variant="soft"
                      startDecorator={<AddIcon />}
                      onClick={() => handleAddJoin(rel, null)}
                    >
                      Add Join
                    </Button>
                  </Sheet>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Nested Join Picker Modal */}
      <Modal open={nestedPicker.isOpen} onClose={closeNestedJoinPicker}>
        <ModalDialog sx={{ minWidth: 500 }}>
          <ModalClose />
          <DialogTitle>
            <Stack direction="row" spacing={1} alignItems="center">
              <AccountTreeIcon />
              <span>Add Nested Join from {nestedPicker.sourceTableName}</span>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography level="body-sm">
                Select a relationship to join from the{" "}
                <strong>{nestedPicker.sourceTableName}</strong> table.
              </Typography>

              {nestedPicker.availableRelationships.length === 0 ? (
                <Alert color="neutral">
                  No available relationships from this table.
                </Alert>
              ) : (
                <RadioGroup
                  value={nestedPicker.selectedRelationshipId || ""}
                  onChange={(e) =>
                    setNestedPicker((prev) => ({
                      ...prev,
                      selectedRelationshipId: e.target.value,
                    }))
                  }
                >
                  <List>
                    {nestedPicker.availableRelationships.map((rel) => {
                      const info = getRelationshipInfo(
                        rel,
                        nestedPicker.sourceTableId,
                      );
                      return (
                        <ListItem key={rel.id}>
                          <ListItemButton
                            onClick={() =>
                              setNestedPicker((prev) => ({
                                ...prev,
                                selectedRelationshipId: rel.id,
                              }))
                            }
                          >
                            <ListItemDecorator>
                              <Radio value={rel.id} />
                            </ListItemDecorator>
                            <ListItemContent>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                flexWrap="wrap"
                              >
                                <Typography level="body-sm" fontWeight="md">
                                  {nestedPicker.sourceTableName}
                                </Typography>
                                <Chip size="sm" variant="soft" color="neutral">
                                  {info.sourceColumnName}
                                </Chip>
                                <ArrowRightAltIcon
                                  sx={{ color: "neutral.400", fontSize: 18 }}
                                />
                                <Typography level="body-sm" fontWeight="md">
                                  {info.targetTableName}
                                </Typography>
                                <Chip size="sm" variant="soft" color="neutral">
                                  {info.targetColumnName}
                                </Chip>
                              </Stack>
                              {rel.relationship_name && (
                                <Typography
                                  level="body-xs"
                                  sx={{ color: "text.tertiary", mt: 0.5 }}
                                >
                                  {rel.relationship_name}
                                </Typography>
                              )}
                            </ListItemContent>
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </RadioGroup>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="plain"
              color="neutral"
              onClick={closeNestedJoinPicker}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleNestedJoinSelect}
              disabled={!nestedPicker.selectedRelationshipId}
              startDecorator={<AddIcon />}
            >
              Add Join
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Stack>
  );
}

/**
 * Recursive component to display a join and its children
 */
interface JoinNodeProps {
  join: ViewBuilderJoin;
  childJoins: ViewBuilderJoin[];
  availableRelationships: SchemaRelationshipResponse[];
  onAddNestedJoin: () => void;
  onRemove: () => void;
  onTypeChange: (type: JoinType) => void;
  getTableName: (tableId: string) => string;
  getRelationshipInfo: (
    rel: SchemaRelationshipResponse,
    sourceTableId: string,
  ) => {
    targetTableId: string;
    targetTableName: string;
    sourceColumnName: string;
    targetColumnName: string;
  };
  getChildJoins: (parentId: string) => ViewBuilderJoin[];
  getAvailableForJoinedTable: (joinId: string) => SchemaRelationshipResponse[];
  openNestedJoinPicker: (joinId: string) => void;
  removeJoin: (joinId: string) => void;
  handleJoinTypeChange: (joinId: string, type: JoinType) => void;
  depth: number;
}

function JoinNode({
  join,
  childJoins,
  availableRelationships,
  onAddNestedJoin,
  onRemove,
  onTypeChange,
  getChildJoins,
  getAvailableForJoinedTable,
  openNestedJoinPicker,
  removeJoin,
  handleJoinTypeChange,
  depth,
}: JoinNodeProps) {
  return (
    <Box sx={{ ml: depth * 3 }}>
      <Sheet
        variant="soft"
        color="primary"
        sx={{
          p: 2,
          borderRadius: "md",
          borderLeft: depth > 0 ? "3px solid" : "none",
          borderColor: "primary.300",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <LinkIcon sx={{ color: "primary.600" }} />

          <Box sx={{ flex: 1 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography level="body-sm" fontWeight="md">
                {join.sourceTableName}
              </Typography>
              <Chip size="sm" variant="outlined" color="primary">
                {join.sourceColumnName}
              </Chip>
              <ArrowRightAltIcon sx={{ color: "primary.400" }} />
              <Typography level="body-sm" fontWeight="md">
                {join.targetTableName}
              </Typography>
              <Chip size="sm" variant="outlined" color="primary">
                {join.targetColumnName}
              </Chip>
            </Stack>
            <Typography
              level="body-xs"
              sx={{ color: "text.tertiary", mt: 0.5 }}
            >
              Alias: <strong>{join.alias}</strong>
            </Typography>
          </Box>

          <Select
            size="sm"
            value={join.joinType}
            onChange={(_, value) => value && onTypeChange(value as JoinType)}
            sx={{ minWidth: 100 }}
          >
            <Option value="LEFT">LEFT</Option>
            <Option value="INNER">INNER</Option>
            <Option value="RIGHT">RIGHT</Option>
            <Option value="FULL">FULL</Option>
          </Select>

          {availableRelationships.length > 0 && (
            <Tooltip title="Add nested join">
              <IconButton
                size="sm"
                variant="soft"
                color="neutral"
                onClick={onAddNestedJoin}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Remove join">
            <IconButton
              size="sm"
              variant="soft"
              color="danger"
              onClick={onRemove}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Sheet>

      {/* Child joins */}
      {childJoins.length > 0 && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {childJoins.map((child) => (
            <JoinNode
              key={child.id}
              join={child}
              childJoins={getChildJoins(child.id)}
              availableRelationships={getAvailableForJoinedTable(child.id)}
              onAddNestedJoin={() => openNestedJoinPicker(child.id)}
              onRemove={() => removeJoin(child.id)}
              onTypeChange={(type) => handleJoinTypeChange(child.id, type)}
              getTableName={() => ""}
              getRelationshipInfo={() => ({
                targetTableId: child.targetTableId,
                targetTableName: child.targetTableName,
                sourceColumnName: child.sourceColumnName,
                targetColumnName: child.targetColumnName,
              })}
              getChildJoins={getChildJoins}
              getAvailableForJoinedTable={getAvailableForJoinedTable}
              openNestedJoinPicker={openNestedJoinPicker}
              removeJoin={removeJoin}
              handleJoinTypeChange={handleJoinTypeChange}
              depth={depth + 1}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
